<!-- User Record Table Component -->
<div class="record-container">
  <div class="header-container">
    <div class="title-container">
      <h5 class="blue-text title-text">{{ title }}</h5>
      @if (showStatusBadge) {
        <status-badge
          [status]="pendingStatus"
          [message]="(customBadgeCount !== null ? customBadgeCount : getPendingTasks(data)) + ' ' + badgeMessage"
          [badgeSize]="statusBadgeSize"></status-badge>
      }
    </div>

    <!-- Custom Button -->
    @if (showButton) {
      <div>
        <button class="header-button" mat-flat-button [disabled]="buttonDisabled" (click)="onAddNewButtonClick()">
          <!-- Left Icon -->
          @if (showIcon && !isSvgIcon) {
            <mat-icon class="add-icon">
              {{ iconName }}
            </mat-icon>
          }
          @if (showIcon && isSvgIcon) {
            <lib-svg-icon
              class="add-icon"
              [svgName]="svgIconName"
              [svgHeight]="'16px'"
              [svgWidth]="'16px'"></lib-svg-icon>
          }
          <!-- Button Text -->
          {{ buttonText }}
        </button>
      </div>
    }
  </div>

  @if (!demo) {
    @switch (recordType) {
      @case (UserRecordType.Comments) {
        <ng-container *ngTemplateOutlet="commentsTemplate"></ng-container>
      }
      @case (UserRecordType.Contacts) {
        <ng-container *ngTemplateOutlet="contactsTemplate"></ng-container>
      }
      @default {
        @if (data.length > 0) {
          <ng-container *ngTemplateOutlet="defaultTemplate"></ng-container>
        }
      }
    }

    <ng-template #commentsTemplate>
       @if (this.isAddComment) {
        <div class="add-comments">
          <textarea #textInternalComment aria-label="Write a comment"
                    class="comments__textarea"
                    matInput maxlength="255" placeholder="Write a comment"
                    style="width: 100% !important; margin-bottom: 16px;"
                    [(ngModel)]="commentBody" [rows]="4">
          </textarea>
          <div class="action-buttons">
            <button class="comments__cancel-btn" mat-flat-button matTooltip="Cancel"
                    type="button" (click)="onCancelAddComment()">
              <span>Cancel</span>
            </button>
            <button class="header-button" mat-flat-button
                    matTooltip="Save comment" type="button"
                    [disabled]="!commentBody.trim()" (click)="onSaveComment()">
              <span>Save</span>
            </button>
          </div>
        </div>
      }
      <div class="comments-display"
           [class.comments-display-view]="isAddComment"
           [style.display]="data.length === 0 ? 'none' : ''">
        @for (comment of commentsData; track comment.id;) {
          <app-comment [comment]="comment"></app-comment>
        }
      </div>
    </ng-template>

    <ng-template #contactsTemplate>
      <div class="table-wrapper">
          <lib-contacts
            [columns]="columnDefinitions"
            [contactBackdropClass]="'edit-contact-dialog-backdrop'"
            [contactDialogPanelClass]="'edit-contact-dialog'"
            [isAdminView]="true"
            [includeHdmaInList]="true"
            [sortKey]="'isPrimaryContact'"
            [userId]="programPartnerId ?? ''"
            [userTypeId]="userTypeId"
            (messageSent)="onMessageSent()">
          </lib-contacts>
        </div>
    </ng-template>

    <ng-template #defaultTemplate>
      <div class="table-wrapper">
        <cfi-mat-table
          [class]="'user-record-table ' + getTableClasses()"
          [scrollable]="scrollable"
          [style.display]="data.length > 0 ? '' : 'none'"
          [data]="displayData"
          [columnDefinitions]="columnDefinitions"
          [columnsToDisplayWithExpand]="displayedColumns"
          [rowClass]="rowClassFn"
          [striped]="false"
          [borderless]="true"
          [paginated]="paginated"
          [currentPageSize]="pageSize"
          [pageSizeOptions]="pageSizeOptions"
          [hidePageSizeSelector]="hidePageSizeSelector"
          [serverSidePaging]="serverSidePaging"
          [sortKey]="sortKey"
          [sortDirection]="sortDirection"
          [displayedColumns]="displayedColumns"
          [(expandedElement)]="expandedElement"
          (expandedElementChange)="onExpandedElementChange($event)"
          [class.table-updated]="tableUpdated"
          (cbSort)="onSortChange($event)">
          <!-- Header Template -->
          <ng-template #headerTemplate let-column>
            <ng-container>
              {{ column.header }}
            </ng-container>
          </ng-template>

          <!-- Cell Template -->
          <ng-template #cellTemplate let-task let-column="column">
            @switch (column.isHyperlink ? '__hyperlink' : column.name) {
              <!-- Opt-in hyperlink column: takes precedence over the per-name cases below -->
              @case ('__hyperlink') {
                <ng-container
                  *ngTemplateOutlet="hyperlinkColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }
              <!-- Bolded Text Columns -->
              @case ('firstName') {
                <ng-container
                  *ngTemplateOutlet="boldedTextColumnTemplate; context: { $implicit: task, column: column, truncateLength: 15 }"></ng-container>
              }
              @case ('lastName') {
                <ng-container
                  *ngTemplateOutlet="boldedTextColumnTemplate; context: { $implicit: task, column: column, truncateLength: 15 }"></ng-container>
              }
              @case ('messageSubject') {
                <ng-container
                  *ngTemplateOutlet="boldedTextColumnTemplate; context: { $implicit: task, column: column, truncateLength: 30 }"></ng-container>
              }
              @case ('childSubject') {
                <ng-container
                  *ngTemplateOutlet="boldedTextColumnTemplate; context: { $implicit: task, column: column, truncateLength: 30 }"></ng-container>
              }
              <!-- Status Column -->
              @case ('status') {
                <ng-container
                  *ngTemplateOutlet="statusColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }
              <!-- Task Status Column -->
              @case ('taskStatus') {
                <ng-container
                  *ngTemplateOutlet="statusColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }
              <!-- Due Date Column -->
              @case ('dueDate') {
                <ng-container
                  *ngTemplateOutlet="dateColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }
              @case ('dateCreated') {
                <ng-container
                  *ngTemplateOutlet="dateColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }
              <!-- Document Column -->
              @case ('document') {
                <td
                  [attr.data-pw]="'cell-' + column.name"
                  [style.font-weight]="task[column.name] === underReviewStatus ? 'bold' : 'normal'"
                  [title]="task[column.name]">
                  <span class="hyperlink">
                    {{ task[column.name] | truncate: 50 }}
                  </span>
                </td>
              }
              <!-- Sub-Service Column -->
              @case ('subServices') {
                <td
                  [attr.data-pw]="'cell-' + column.name"
                  [title]="task[column.name]">
                  @for (subService of task[column.name]; track subService.id) {
                    {{ subService.name | truncate: 50 }}
                  }
                </td>
              }
              <!-- isPrimary Column -->
              @case ('isPrimary') {
                <td [attr.data-pw]="'cell-' + column.name" [title]="task[column.name]">
                  @if (task[column.name]) {
                    <lib-svg-icon
                      class="primary-icon"
                      [svgName]="isPrimaryIcon"
                      [svgHeight]="'18px'"
                      [svgWidth]="'18px'"></lib-svg-icon>
                  } @else {
                    <lib-svg-icon
                      class="primary-icon"
                      [svgName]="isNotPrimaryIcon"
                      [svgHeight]="'18px'"
                      [svgWidth]="'18px'"></lib-svg-icon>
                  }
                </td>
              }
              <!-- On Hold Status Column -->
              @case ('onHoldStatus') {
                <ng-container
                  *ngTemplateOutlet="onHoldStatusColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }

              <!-- Reported Date & Last Message Date Columns -->
              @case ('dateReported') {
                <ng-container
                  *ngTemplateOutlet="dateColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }
              @case ('lastMessageDate') {
                <ng-container
                  *ngTemplateOutlet="dateColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }
              @case('startDate') {
                <ng-container
                  *ngTemplateOutlet="dateColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }
              <!-- Action Column -->
              @case ('action') {
                <ng-container
                  *ngTemplateOutlet="actionColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }

              <!-- Issue Tracking Action Column -->
              @case ('issueTrackingAction') {
                <ng-container
                  *ngTemplateOutlet="actionColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }

              <!-- Chevron Column -->
              @case ('chevron') {
                <ng-container
                  *ngTemplateOutlet="chevronColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }

              @case ('schoolName') {
                <ng-container
                  *ngTemplateOutlet="toolTipColumnTemplate; context: { $implicit: task, column: column }"></ng-container>
              }

              @case ('studentName') {
                <td
                  [attr.data-pw]="'cell-' + column.name"
                  [title]="task[column.name]">
                    @if (task.studentName !== null) {
                      <a
                        class="student hyperlink view-more-label"
                        style="cursor: pointer; user-select: none"
                        href="javascript:void(0)"
                        (click)="onStudentNameClick({ studentId: task.id, studentName: task.studentName }); $event.stopPropagation()">
                          {{ task.studentName }}
                      </a>
                    }
                    @else {
                      {{ '-' }}
                    }
                </td>
              }

              @case ('osAwardAmountOffered') {
                <td class="award-offer"
                  [attr.data-pw]="'cell-' + column.name"
                  [title]="task[column.name]">
                  @if (task.osAwardAmountOffered !== null) {
                     {{ task.osAwardAmountOffered | currency:'USD':'symbol':'1.0-0' }} <span>{{ task.osAwardStatus }}</span>
                  }
                  @else {
                      {{'-'}}
                  }
                </td>
              }

              @case ('esaAwardAmountOffered') {
                <td class="award-offer"
                  [attr.data-pw]="'cell-' + column.name"
                  [title]="task[column.name]">
                  @if (task.esaAwardAmountOffered !== null) {
                     {{ task.esaAwardAmountOffered | currency:'USD':'symbol':'1.0-0' }} <span>{{ task.esaAwardStatus }}</span>
                  }
                  @else {
                      {{'-'}}
                  }
                </td>
              }

              @case ('grade') {
                <td
                  [attr.data-pw]="'cell-' + column.name"
                  [style.font-weight]="'normal'"
                  [title]="task[column.name]">
                   {{ task.grade?.replace('Grade', '')?.trim() || '-' }}
                </td>
              }

              <!-- Default Column -->
              @default {
                <div
                  class="user-record-cell--text-wrap"
                  [attr.data-pw]="'cell-' + column.name"
                  [style.font-weight]="task[column.name] === underReviewStatus ? 'bold' : 'normal'"
                  [title]="task[column.name] || ('New ' + title).slice(0, -1)">
                  @if(recordType === UserRecordType.Students)
                  {
                    {{ (task[column.name] ?? '-') }}
                  }
                  @else
                  {
                    {{ task[column.name] }}
                  }
                </div>
              }
            }
          </ng-template>

          <!-- Hyperlink Column Template -->
          <ng-template #hyperlinkColumnTemplate let-task let-column="column">
            <td [attr.data-pw]="'cell-' + column.name" [title]="task[column.name]">
              <a
                class="hyperlink view-more-label"
                style="cursor: pointer; user-select: none"
                href="javascript:void(0)"
                (click)="onCellLinkClick(task, column.name); $event.stopPropagation()">
                {{ task[column.name] || '-' }}
              </a>
            </td>
          </ng-template>

          <!-- Status Column Template -->
          <ng-template #statusColumnTemplate let-task let-column="column">
            <td
              [class]="getStatusClass(task[column.name])"
              data-pw="statusCell"
              [attr.data-pw]="'cell-' + column.name"
              [style.font-weight]="redStatuses.includes(task[column.name]) || greenStatuses.includes(task[column.name]) ? 'bold' : 'normal'"
              [title]="task[column.name]">
              {{ task[column.name] | truncate: 40 }}
            </td>
          </ng-template>

          <!-- createdDate Column Template -->
          <ng-template #dateColumnTemplate let-task let-column="column">
            <td
            [attr.data-pw]="'cell-' + column.name"
            [style.font-weight]="(task[column.name] === underReviewStatus || (task['unreadCount'] && task['unreadCount'] > 0)) ? 'bold' : 'normal'"
            [title]="task[column.name] | date: 'MM/dd/yyyy'">
              {{ task[column.name] | date: 'MM/dd/yyyy' }}
            </td>
          </ng-template>

          <!-- onHoldStatus Column Template -->
          <ng-template #onHoldStatusColumnTemplate let-task let-column="column">
            <td [attr.data-pw]="'cell-' + column.name" [title]="task[column.name]">
              <span>{{ getRestrictedIssueTypesText(task) }}</span>
            </td>
          </ng-template>

          <!-- Action Column Template -->
          <ng-template #actionColumnTemplate let-task let-column="column">
            <td [attr.data-pw]="'cell-' + column.name" title="Edit">
              <button
                  aria-label="Edit Issue Tracking"
                  class="edit-issue-action"
                  mat-icon-button
                  [attr.data-qa]="'edit-issue' + task.id"
                  [disabled]="!isRowActionEditEnabled(task)"
                  (click)="column.name === 'issueTrackingAction' ? onAddNewButtonClick(task) : onAddNewButtonClick()">
                <mat-icon class="mat-action-icon" svgIcon="k12-edit"></mat-icon>
              </button>
            </td>
          </ng-template>

          <!-- Chevron Column Template -->
          <ng-template #chevronColumnTemplate let-task let-column="column">
            <td [attr.data-pw]="'cell-' + column.name" [title]="task[column.name]">
              <div [attr.data-pw]="'cell-' + column.name">
                <!-- Empty space for tasks without subtasks, chevron is handled by cfi-mat-table -->
              </div>
            </td>
          </ng-template>

          <!-- Bolded Text Column Template -->
          <ng-template #boldedTextColumnTemplate let-task let-column="column" let-truncateLength="truncateLength">
            <td
              [attr.data-pw]="'cell-' + column.name"
              [style.font-weight]="
                (task[column.name] === underReviewStatus || 
                (task['unreadCount'] && task['unreadCount'] > 0)) 
                  ? 'bold' : 'normal'"
              [title]="task[column.name]">
              {{ task[column.name] | truncate: truncateLength }}
            </td>
          </ng-template>

          <!-- Tool Tip Column Template -->
          <ng-template #toolTipColumnTemplate let-task let-column="column">
            <td
              [attr.data-pw]="'cell-' + column.name"
              [title]="task[column.name] || '-'"
              matTooltip="{{ task[column.name] || '-' }}"
              matTooltipPosition="above">
                {{ (task[column.name] || '-') | truncate: 20 }}
            </td>
          </ng-template>

          <!-- Expanded Content Template -->
          <ng-template #expandedRow let-subCell>
            @for (column of columnDefinitions; track column.name; let i = $index) {
              <div 
                class="table-cell__icon-container" 
                [attr.data-pw]="'expandedCell-' + column.name"
                [style.width.%]="column.expandedWidth"
                [class.centered-cell]="column.centered">
                <div class="subtask-content">
                    <ng-container>
                      @switch (column.name) {
                        @case ('dateReported') {
                          <ng-container
                            *ngTemplateOutlet="dateColumnTemplate; context: { $implicit: subCell, column: column }"></ng-container>
                        }
                        @case ('lastMessageDate') {
                          <ng-container
                            *ngTemplateOutlet="dateColumnTemplate; context: { $implicit: subCell, column: column }"></ng-container>
                        }
                        @case ('onHoldStatus') {
                          <ng-container
                            *ngTemplateOutlet="onHoldStatusColumnTemplate; context: { $implicit: subCell, column: column }"></ng-container>
                        }
                        @case ('action') {
                          <ng-container
                            *ngTemplateOutlet="actionColumnTemplate; context: { $implicit: subCell, column: column }"></ng-container>
                        }
                        @case ('issueTrackingAction') {
                          <ng-container
                            *ngTemplateOutlet="actionColumnTemplate; context: { $implicit: subCell, column: column }"></ng-container>
                        }
                        @case ('chevron') {
                          <ng-container
                            *ngTemplateOutlet="chevronColumnTemplate; context: { $implicit: subCell, column: column }"></ng-container>
                        }
                        @default {
                          <div
                            class="user-record-cell--text-wrap"
                            [attr.data-pw]="'cell-' + column.name"
                            [title]="subCell[column.name]">
                            {{ subCell[column.name] }}
                          </div>
                        }
                      }
                  </ng-container>
                </div>
              </div>
            }
          </ng-template>
        </cfi-mat-table>
      </div>
    </ng-template>

    @if (data.length === 0 && recordType !== UserRecordType.Contacts) { 
      <div class="grid-col-12 grid-md:col-12 empty-panel user-record-table"
           [style.display]="data.length > 0 ? 'none' : ''">
           <span class="neutral-text">
            No {{ emptyLabel || title }}
          </span>
      </div>
    }
   
    <!-- Add a view more hyperlink -->
    @if (showViewMore) {
      <div class="view-more" [class.margin-top-2]="!isInternalComments" [class.margin-top-4]="isInternalComments">
        <a
          class="hyperlink view-more-label"
          style="cursor: pointer; user-select: none"
          href="javascript:void(0)"
          (click)="onViewMoreClick(); $event.stopPropagation()">
            {{ getViewToggleLabel() }}
        </a>
      </div>
    }
  }